interface SectionHeaderProps {
  /** Small uppercase eyebrow text above the title */
  eyebrow?: string
  /** Main headline */
  title: string
  /** Supporting description text */
  description?: string
  /** Additional classes on the wrapper */
  className?: string
  /** Right-side content (e.g. nav buttons, "View all" link) */
  actions?: React.ReactNode
  /** Whether to show a bottom border */
  bordered?: boolean
  /**
   * Visual variant:
   * - "default" — Large display text, left-aligned (product detail, collection pages)
   * - "light" — Smaller headline, left-aligned (directory/listing pages)
   * - "centered" — Centered text with optional accent bar (index pages)
   */
  variant?: "default" | "light" | "centered"
}

export function SectionHeader({
  eyebrow,
  title,
  description,
  className = "",
  actions,
  bordered = false,
  variant = "default",
}: SectionHeaderProps) {
  if (variant === "centered") {
    return (
      <div className={`text-center ${bordered ? "border-b border-gray-200 pb-8 mb-10" : "mb-6"} ${className}`}>
        {eyebrow && (
          <span className="text-[#ff9951] font-bold tracking-widest text-xs mb-2 block uppercase">
            {eyebrow}
          </span>
        )}
        <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">
          {title}
        </h2>
        {description && (
          <p className="text-base text-gray-500 max-w-2xl mx-auto leading-relaxed">
            {description}
          </p>
        )}
        {/* Optional centered accent bar */}
        <div className="w-12 h-0.5 bg-[#0469f0]/20 mx-auto mt-4" />
        {actions && <div className="flex justify-center gap-3 mt-4">{actions}</div>}
      </div>
    )
  }

  if (variant === "light") {
    return (
      <div
        className={`flex flex-col md:flex-row justify-between items-start md:items-end gap-4 ${
          bordered ? "border-b border-gray-200 pb-6 mb-8" : "mb-6"
        } ${className}`}
      >
        <div>
          {eyebrow && (
            <span className="text-[#ff9951] font-bold tracking-widest text-xs mb-2 block uppercase">
              {eyebrow}
            </span>
          )}
          <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-1">
            {title}
          </h2>
          {description && (
            <p className="text-sm text-gray-500 max-w-md leading-relaxed">
              {description}
            </p>
          )}
        </div>
        {actions && <div className="flex gap-3">{actions}</div>}
      </div>
    )
  }

  // Default variant — matches hub page section headers
  return (
    <div
      className={`flex flex-col md:flex-row justify-between items-start md:items-end gap-4 ${
        bordered ? "border-b border-gray-200 pb-8 mb-10" : "mb-6"
      } ${className}`}
    >
      <div>
        {eyebrow && (
          <span className="text-[#ff9951] font-bold tracking-widest text-xs mb-2 block uppercase">
            {eyebrow}
          </span>
        )}
        <h2 className="text-2xl md:text-3xl font-black tracking-tighter leading-tight text-gray-900">
          {title}
        </h2>
        {description && (
          <p className="text-gray-500 max-w-md text-sm leading-relaxed">
            {description}
          </p>
        )}
      </div>
      {actions && <div className="flex gap-3">{actions}</div>}
    </div>
  )
}
